import i18n from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router-dom';
import { forkJoin, of, range } from 'rxjs';
import { map, tap, switchMap, toArray } from 'rxjs/operators';

import StepIndicator from '../../../../components/common/step-indicator';
import { SubmitType } from '../../../../components/form';
import Amount from '../../../../components/form/elements/amount';
import Disabled from '../../../../components/form/elements/disabled';
import Select, { Option } from '../../../../components/form/elements/select';
import Watch from '../../../../components/form/elements/watch';
import { CropWorkType } from '../../../../enums/crop-work-types';
import { useQuery } from '../../../../hooks/use-query';
import ClassifierValue from '../../../../interfaces/classifierValue';
import Employee from '../../../../interfaces/employee';
import { AllEquipmentFormFields } from '../../../../interfaces/equipment';
import Paginate from '../../../../interfaces/paginate';
import { Product } from '../../../../interfaces/product';
import Warehouse from '../../../../interfaces/warehouse';
import Work from '../../../../interfaces/work';
import { WorkProduct } from '../../../../interfaces/work-product';
import {
  CROP_FIELDS_AND_WORKS_INDEX,
  CROP_WORK_VIEW,
} from '../../../../router/routes';
import { RemainderService } from '../../../../services/remainder.service';
import { useService } from '../../../../services/services';
import { deepCopy, route } from '../../../../services/utils';
import { WarehouseService } from '../../../../services/warehouse.service';
import StepDetails, { DetailsSubmitData } from './step-details';
import StepOtherProducts from './step-other-products';
import StepOverview from './step-overview';
import StepSowingProducts from './step-sowing-products';
import StepSprayingProducts from './step-spraying-products';
import StepWork, { WorkSubmitData } from './step-work';
import { Section } from '../../../../pages/crop/fields-and-works';
import { WorkProductWarehouse } from '../../../../interfaces/work-product-warehouse';
import Remainder from '../../../../interfaces/remainder';
import DealType from '../../../../enums/deal-type';
import ClassifierPropertyKey from '../../../../enums/classifier-property-key';
import ClassifierValueProperty from '../../../../interfaces/classifierValueProperty';
import { WorkStepKey } from '../../../../enums/work-step-keys';
import Field from '../../../../interfaces/field';
import toFixed from 'accounting-js/lib/toFixed.js';

export interface ProductsSubmitData {
  products: {
    id: string;
    crop_group: Option & { cropGroup: ClassifierValue };
    product_category: Option & { productCategory: ClassifierValue };
    product_type: Option & { productType: ClassifierValue };
    product: Option & { product: Product };
    reason_of_use?: string;
    is_intercrop: boolean;
    is_interculture: boolean;
    warehouses: {
      warehouse: Option & { warehouse: Warehouse };
      seeding_rate_ha: string;
      seeding_rate_ha_unit: Option;
      quantity: number;
      quantity_unit: Option;
      remainder: number;
      remainder_unit: Option;
    }[];
  }[];
  intercrop: {
    id: string;
    crop_group: Option & { cropGroup: ClassifierValue };
    product_category: Option & { productCategory: ClassifierValue };
    product_type: Option & { productType: ClassifierValue };
    product: Option & { product: Product };
    is_intercrop: boolean;
    warehouses: {
      warehouse: Option & { warehouse: Warehouse };
      seeding_rate_ha: string;
      seeding_rate_ha_unit: Option;
      quantity: number;
      quantity_unit: Option;
      remainder: number;
      remainder_unit: Option;
    }[];
  }[];
}

export interface FormData
  extends WorkSubmitData,
    DetailsSubmitData,
    ProductsSubmitData {
  farm_id: string;
  id: string;
  type_id: CropWorkType;
  type_name: string;
}

export function formDataToWork(formData: FormData) {
  const work = {
    farm_id: formData.farm_id,
    id: formData.id,
    type_id: formData.type_id,
    work_id: formData.work.value,
    name: formData.name,
    date: formData.date,
    done: formData.done,
    duration: formData.duration,
    notes: formData.notes,
  } as Work;
  if (formData.files?.length > 0) {
    work.files = formData.files;
  }
  if (formData.fields) {
    work.fields = formData.fields
      .filter((field) => field?.field?.field)
      .map((field) => {
        return {
          id: field.field.value,
          treated_area: field.treated_area,
          plant_development_stage_id: field.plant_development_stage?.value,
        } as any;
      });
  }
  if (formData.employees) {
    work.employees = formData.employees?.map((employee) => {
      return { id: employee.value } as Employee;
    });
  }
  if (formData.equipment) {
    work.equipment = formData.equipment?.map((equipment) => {
      return { id: equipment.value } as AllEquipmentFormFields;
    });
  }
  if (formData.products) {
    work.products = formData.products
      ?.filter((product) => product?.product?.value)
      .map((product) => {
        return {
          id: product.id,
          product: {
            id: product.product.value,
            reason_of_use: product.reason_of_use,
            is_interculture: product.is_interculture,
          } as Product & any,
          work_product_warehouses: product.warehouses
            ?.filter((warehouse) => warehouse?.warehouse?.value)
            .map((warehouse) => {
              return {
                warehouse_id: warehouse.warehouse.value,
                quantity: warehouse.quantity,
                unit: warehouse.quantity_unit.value,
              };
            }),
        } as WorkProduct;
      });
  }

  if (formData.intercrop) {
    work.intercrop = formData.intercrop
      ?.filter((product) => product?.product?.value)
      .map((product) => {
        return {
          id: product.id,
          product: {
            id: product.product.value,
          } as Product & any,
          work_product_warehouses: product.warehouses
            ?.filter((warehouse) => warehouse?.warehouse?.value)
            .map((warehouse) => {
              return {
                warehouse_id: warehouse.warehouse.value,
                quantity: warehouse.quantity,
                unit: warehouse.quantity_unit.value,
              };
            }),
        } as WorkProduct;
      });
  }

  return work;
}

export function workToFormData(work: Work) {
  const treatedArea = work.fields.reduce((sum: number, obj: any) => {
    return sum + parseFloat(obj.pivot.treated_area);
  }, 0);

  const formData = {
    farm_id: work.farm_id,
    id: work.id,
    type_id: work.type_id,
    name: work.name,
    date: work.planned ? null : work.date,
    created_at: work.created_at,
    done: work.done,
    duration: work.duration,
    notes: work.notes,
    files: work.files,
    type_name: i18n.t(
      'classifiers.properties.crop_work_type.'.concat(work.type_id)
    ),
    work: {
      value: work.work.id,
      label: work.work.value,
      work: work.work,
    },
    fields: work.fields.map((field) => {
      let plantDevelopmentStage: Option | null = null;

      if (field.plant_development_stage) {
        const descriptionProperty = Object.values(
          field.plant_development_stage.properties
        ).find(
          (property) =>
            property.property.key ===
            ClassifierPropertyKey.PLANT_DEVELOPMENT_STAGE_DESCRIPTION
        ) as ClassifierValueProperty;

        plantDevelopmentStage = {
          label: field.plant_development_stage.value.concat(
            ' - ',
            descriptionProperty.value
          ),
          value: field.plant_development_stage.id,
        };
      }

      return {
        field: { value: field.id, label: field.name, field: field },
        lad_number: field.lad_number,
        area: field.area,
        treated_area: field.pivot.treated_area,
        crop_value: field.crop_value,
        plant_development_stage: plantDevelopmentStage,
        works: field.works,
      };
    }),
    products: work.products
      .filter((product) => !product.is_intercrop)
      .map((product) => {
        const cropGroup = Object.values(
          product.product.category.properties
        ).find(
          (property) =>
            property.property.key === ClassifierPropertyKey.CROP_GROUP
        );
        return {
          id: product.id,
          crop_group: {
            value: cropGroup.value[0].id,
            label: cropGroup.value[0].label,
          },
          product_category: {
            value: product.product.category.id,
            label: product.product.category.value,
            productCategory: product.product.category,
          },
          product_type: {
            value: product.product.type.id,
            label: product.product.type.value,
            productType: product.product.type,
          },
          product: {
            value: product.product.id,
            label: product.product.name,
            product: product.product,
          },
          reason_of_use: product.reason_of_use,
          is_interculture: product.is_interculture,
          is_intercrop: product.is_intercrop,
          warehouses: product.work_product_warehouses.map((warehouse) => {
            return {
              warehouse: {
                value: warehouse.warehouse.id,
                label: warehouse.warehouse.name,
                warehouse: warehouse.warehouse,
              },
              quantity: warehouse.quantity,
              quantity_unit: { value: warehouse.unit, label: warehouse.unit },
              seeding_rate_ha: toFixed(warehouse.quantity / treatedArea, 3),
              seeding_rate_ha_unit: {
                value: warehouse.unit,
                label: warehouse.unit,
              },
            };
          }),
        };
      }),
    intercrop: work.products
      .filter((product) => product.is_intercrop)
      .map((product) => {
        const cropGroup = Object.values(
          product.product.category.properties
        ).find(
          (property) =>
            property.property.key === ClassifierPropertyKey.CROP_GROUP
        );
        return {
          id: product.id,
          crop_group: {
            value: cropGroup.value[0].id,
            label: cropGroup.value[0].label,
          },
          product_category: {
            value: product.product.category.id,
            label: product.product.category.value,
            productCategory: product.product.category,
          },
          product_type: {
            value: product.product.type.id,
            label: product.product.type.value,
            productType: product.product.type,
          },
          product: {
            value: product.product.id,
            label: product.product.name,
            product: product.product,
          },
          is_intercrop: product.is_intercrop,
          warehouses: product.work_product_warehouses.map((warehouse) => {
            return {
              warehouse: {
                value: warehouse.warehouse.id,
                label: warehouse.warehouse.name,
                warehouse: warehouse.warehouse,
              },
              quantity: warehouse.quantity,
              quantity_unit: { value: warehouse.unit, label: warehouse.unit },
              seeding_rate_ha: toFixed(warehouse.quantity / treatedArea, 3),
              seeding_rate_ha_unit: {
                value: warehouse.unit,
                label: warehouse.unit,
              },
            };
          }),
        };
      }),
    employees: work.employees.map((employee) => {
      return {
        value: employee.id,
        label: employee.full_name,
        employee: employee,
      };
    }),
    equipment: work.equipment.map((equipment) => {
      return {
        value: equipment.id,
        label: equipment.label,
        equipment: equipment,
      };
    }),
  } as FormData;
  return formData;
}

export function ProductSelectWarehouses(
  pType: string,
  type: string,
  tp: any,
  farmId: string,
  warehouseService: WarehouseService,
  remainderService: RemainderService,
  productTypeIds?: string[]
) {
  let canUpdateQuantity = true;
  let canUpdateSeedingRate = true;
  interface RemainderResponse {
    data: {
      amount?: string;
      unit?: string;
    }[];
  }
  return {
    name: 'warehouses',
    title: tp('warehouses'),
    components: () => [
      Disabled(
        Watch(
          Select('warehouse', {
            asyncOptions: (
              value,
              loadedOptions,
              { page, parentName, getValues, setValue }
            ) => {
              const params: any = {
                page,
                value,
                deal_type: [DealType.produced, DealType.purchased],
              };

              const fieldKey = parentName.split('.')[1];
              const product = getValues(
                pType.concat('.', fieldKey, '.product')
              );
              const warehouses = getValues(
                pType.concat('.', fieldKey, '.warehouses')
              );
              const usedWarehouses = warehouses
                .map((warehouse: any) => warehouse.warehouse.value)
                .filter((value: string) => value !== '');

              if (usedWarehouses) {
                params.except = usedWarehouses;
              }

              if (product?.value) {
                params.product = product?.value;
              }
              if (productTypeIds) {
                params.product_type = productTypeIds;
              }
              if (getValues('products.0.product_category')) {
                params.product_category = [
                  getValues('products.0.product_category').value,
                ];
              }
              return warehouseService.index(farmId, params).pipe(
                tap((response: Paginate<Warehouse>) => {
                  if (response.data.length === 1) {
                    const selectedWarehouse = {
                      label: response.data[0].name,
                      value: response.data[0].id,
                      warehouse: response.data[0],
                      remainder: { amount: '', unit: '' },
                    };

                    const isProductChosen = !!getValues(
                      pType.concat('.', parentName.split('.')[1], '.product')
                    )?.value;

                    if (isProductChosen) {
                      setValue(parentName + '.warehouse', selectedWarehouse);

                      remainderService
                        .index(farmId, {
                          product_id: params.product,
                          warehouse_id: selectedWarehouse.value,
                        })
                        .subscribe((remainderResponse: RemainderResponse) => {
                          selectedWarehouse.remainder = {
                            amount: remainderResponse.data[0]?.amount || '',
                            unit: remainderResponse.data[0]?.unit || '',
                          };
                          setValue(
                            parentName + '.remainder',
                            selectedWarehouse.remainder.amount
                          );
                          setValue(parentName + '.remainder_unit', {
                            value: selectedWarehouse.remainder.unit,
                            label: selectedWarehouse.remainder.unit,
                          });
                          setValue(parentName + '.seeding_rate_ha_unit', {
                            value: selectedWarehouse.remainder.unit || '',
                            label: selectedWarehouse.remainder.unit || '',
                          });
                          setValue(parentName + '.quantity_unit', {
                            value: selectedWarehouse.remainder.unit || '',
                            label: selectedWarehouse.remainder.unit || '',
                          });
                        });
                    }
                  }
                }),
                map((response: Paginate<Warehouse>) => ({
                  hasMore: response.last_page > response.current_page,
                  options: response.data.map((warehouse) => ({
                    label: warehouse.name,
                    value: warehouse.id,
                    warehouse: warehouse,
                    remainder: { amount: '', unit: '' },
                  })),
                  additional: { page: response.current_page + 1 },
                }))
              );
            },
            multi: false,
            label: tp('warehouse'),
            cacheOn: ['_'.concat(pType, '__1__product')],
          }),
          ({ value, parentName, isTouched, setValue, getValues, trigger }) => {
            setValue(parentName.concat('.warehouse_id'), value[0]?.value);
            if (!isTouched) {
              return;
            }
            const productId = getValues(
              pType.concat('.', parentName.split('.')[1], '.product')
            )?.value;

            if (productId) {
              remainderService
                .index(farmId, {
                  product_id: productId,
                  warehouse_id: value[0]?.value,
                })
                .subscribe((response) => {
                  value[0].remainder = {
                    amount: response.data[0]?.amount,
                    unit: response.data[0]?.unit,
                  };
                  setValue(
                    parentName.concat('.remainder'),
                    response.data[0]?.amount
                  );
                  if (getValues(parentName.concat('.quantity'))) {
                    trigger(parentName.concat('.quantity'));
                  }

                  setValue(parentName.concat('.remainder_unit'), {
                    value: response.data[0]?.unit || '',
                    label: response.data[0]?.unit || '',
                  });
                  setValue(parentName.concat('.quantity_unit'), {
                    value: response.data[0]?.unit || '',
                    label: response.data[0]?.unit || '',
                  });
                  setValue(parentName.concat('.seeding_rate_ha_unit'), {
                    value: response.data[0]?.unit || '',
                    label: response.data[0]?.unit || '',
                  });
                });
            }
          },
          false
        ),
        {
          watchOn: '_'.concat(pType, '__1__product'),
          callback: (product) => (product?.value ? false : true),
        }
      ),
      Disabled(
        Amount(
          { amount: 'remainder', unit: 'remainder_unit' },
          {
            asyncOptions: async (
              value,
              loadedOptions,
              { getValues, parentName }
            ) => ({
              hasMore: false,
              options: [
                {
                  label:
                    getValues(parentName.concat('.warehouse'))?.remainder
                      ?.unit || '',
                  value:
                    getValues(parentName.concat('.warehouse'))?.remainder
                      ?.unit || '',
                },
              ],
              additional: {},
            }),
            cacheOn: ['warehouse.value'],
            onDisabled: 'both',
            label: tp('remainder'),
          }
        )
      ),
      Disabled(
        Watch(
          Amount(
            { amount: 'seeding_rate_ha', unit: 'seeding_rate_ha_unit' },
            {
              asyncOptions: async (
                value,
                loadedOptions,
                { getValues, parentName }
              ) => ({
                hasMore: false,
                options: [
                  {
                    label:
                      getValues(parentName.concat('.warehouse'))?.remainder
                        ?.unit || '',
                    value:
                      getValues(parentName.concat('.warehouse'))?.remainder
                        ?.unit || '',
                  },
                ],
                additional: {},
              }),
              cacheOn: ['warehouse.value'],
              onDisabled: 'both',
              disableUnit: true,
              label:
                type === CropWorkType.SOWING_OR_PLANTING
                  ? tp('seeding_rate_ha')
                  : tp('gave_ha'),
            }
          ),
          ({ value: [seeding_rate_ha], parentName, setValue, getValues }) => {
            if (canUpdateQuantity && seeding_rate_ha !== undefined) {
              canUpdateSeedingRate = false;

              const treatedArea = getValues('fields').reduce(
                (sum: number, obj: any) => {
                  return sum + obj.treated_area;
                },
                0
              );

              setValue(
                parentName + '.quantity',
                seeding_rate_ha
                  ? toFixed(seeding_rate_ha * treatedArea, 3)
                  : '',
                { shouldValidate: true }
              );
            }
            canUpdateQuantity = true;
          }
        ),
        {
          watchOn: '_'.concat(pType, '__1__product'),
          callback: (product) => (product?.value ? false : true),
        }
      ),
      Disabled(
        Watch(
          Amount(
            {
              amount: 'quantity',
              unit: 'quantity_unit',
              maxAllowedAmountRefName: 'remainder',
            },
            {
              asyncOptions: async (
                value,
                loadedOptions,
                { getValues, parentName }
              ) => ({
                hasMore: false,
                options: [
                  {
                    label:
                      getValues(parentName.concat('.warehouse'))?.remainder
                        ?.unit || '',
                    value:
                      getValues(parentName.concat('.warehouse'))?.remainder
                        ?.unit || '',
                  },
                ],
                additional: {},
              }),
              cacheOn: ['warehouse.value'],
              onDisabled: 'both',
              disableUnit: true,
              label: tp('quantity'),
            }
          ),
          ({ value: [quantity], parentName, setValue, getValues }) => {
            if (canUpdateSeedingRate && quantity !== undefined) {
              canUpdateQuantity = false;

              const treatedArea = getValues('fields').reduce(
                (sum: number, obj: any) => {
                  return sum + obj.treated_area;
                },
                0
              );

              setValue(
                parentName + '.seeding_rate_ha',
                quantity ? toFixed(quantity / treatedArea, 3) : ''
              );
            }
            canUpdateSeedingRate = true;
          }
        ),
        {
          watchOn: '_'.concat(pType, '__1__product'),
          callback: (product) => (product?.value ? false : true),
        }
      ),
    ],
  };
}

export default function CreateWork() {
  const history = useHistory();
  const { farmId, season, type, id } = useParams<{
    farmId: string;
    season: string;
    type: CropWorkType;
    id: string;
  }>();
  const [params] = useQuery();
  const { t } = useTranslation();
  const tp = useCallback(
    (key: string) => t('pages.crop.works.'.concat(key)),
    [t]
  );
  const { fieldService, workService, remainderService } = useService();

  if (!type && !id) {
    history.push(
      route(CROP_FIELDS_AND_WORKS_INDEX, {
        farmId: farmId,
        season: season,
        tab: Section.fields,
      })
    );
  }

  const [warehouseProductCount, setWarehouseProductCount] = useState<number>(0);
  const [updatedRemaindersCount, setUpdatedRemaindersCount] =
    useState<number>(0);

  const [formData, setFormData] = useState<FormData>(null);
  const [step, setStep] = useState(0);
  const [stepKey, setStepKey] = useState(WorkStepKey.WORK);

  useEffect(() => {
    // edit page
    if (id) {
      workService.show(farmId, id).subscribe((work: Work) => {
        setWarehouseProductCount(
          work.products.reduce(
            (previousValue, currentValue, currentIndex, array) => {
              return (
                previousValue +
                array[currentIndex].work_product_warehouses.length
              );
            },
            0
          )
        );

        const data = workToFormData(work);

        let count = 0;

        Object.values(work.products)
          .filter((product) => !product.is_intercrop)
          .forEach((product: WorkProduct, productIndex) => {
            product.work_product_warehouses.forEach(
              (warehouse: WorkProductWarehouse, warehouseIndex) => {
                remainderService
                  .index(work.farm_id, {
                    product_id: product.product_id,
                    warehouse_id: warehouse.warehouse_id,
                  })
                  .subscribe((remainder: Paginate<Remainder>) => {
                    data.products[productIndex].warehouses[
                      warehouseIndex
                    ].remainder =
                      parseFloat(remainder.data[0]?.amount) +
                      warehouse.quantity;

                    data.products[productIndex].warehouses[
                      warehouseIndex
                    ].remainder_unit = {
                      value: remainder.data[0]?.unit,
                      label: remainder.data[0]?.unit,
                    };
                    setFormData(data);
                    count++;
                    setUpdatedRemaindersCount(count);
                  });
              }
            );
          });

        Object.values(work.products)
          .filter((product) => product.is_intercrop)
          .forEach((product: WorkProduct, productIndex) => {
            product.work_product_warehouses.forEach(
              (warehouse: WorkProductWarehouse, warehouseIndex) => {
                remainderService
                  .index(work.farm_id, {
                    product_id: product.product_id,
                    warehouse_id: warehouse.warehouse_id,
                  })
                  .subscribe((remainder: Paginate<Remainder>) => {
                    data.intercrop[productIndex].warehouses[
                      warehouseIndex
                    ].remainder = parseFloat(remainder.data[0]?.amount);
                    data.intercrop[productIndex].warehouses[
                      warehouseIndex
                    ].remainder_unit = {
                      value: remainder.data[0]?.unit,
                      label: remainder.data[0]?.unit,
                    };
                    setFormData(data);
                    count++;
                    setUpdatedRemaindersCount(count);
                  });
              }
            );
          });
        setFormData(data);
      });
    }
    // create page
    else {
      setFormData((state) => {
        return {
          ...state,
          farm_id: farmId,
          type_id: type,
          type_name: t('classifiers.properties.crop_work_type.'.concat(type)),
          fields: [],
        } as FormData;
      });
      const fieldIds = params['fields'] ? params['fields'].split(',') : null;
      if (fieldIds) {
        const subscription = range(0, fieldIds.length)
          .pipe(
            map((index) => fieldService.show(farmId, fieldIds[index])),
            toArray(),
            switchMap((array) => forkJoin(array))
          )
          .subscribe((response: Field[]) => {
            const workTypeIsAllowed = workService.workTypeIsAllowedForFields(
              type,
              response
            );

            if (!workTypeIsAllowed) {
              history.push(
                route(CROP_FIELDS_AND_WORKS_INDEX, {
                  farmId: farmId,
                  season: season,
                  tab: Section.fields,
                })
              );
            }

            const fields = response
              .sort((fieldA, fieldB) =>
                fieldA.lad_number.localeCompare(fieldB.lad_number, undefined, {
                  numeric: true,
                })
              )
              .map((field: Field) => {
                return {
                  field: {
                    value: field.id,
                    label: field.name,
                    field: field as Field,
                  },
                  lad_number: field.lad_number,
                  area: field.area,
                  treated_area: field.area,
                  crop_value: field.crop_value,
                  delete_field_crop: undefined,
                  works: [],
                };
              });
            setFormData((state) => {
              return { ...state, fields: fields };
            });
          });
        return () => subscription.unsubscribe();
      }
    }
  }, [
    t,
    farmId,
    type,
    id,
    params,
    workService,
    fieldService,
    remainderService,
    history,
    season,
  ]);

  const returnStep = () => {
    switch (stepKey) {
      case WorkStepKey.OVERVIEW:
        setStepKey(WorkStepKey.DETAILS);
        break;
      case WorkStepKey.DETAILS:
        switch (formData.type_id) {
          case CropWorkType.SOWING_OR_PLANTING:
            setStepKey(WorkStepKey.SOWING_PRODUCTS);
            break;
          case CropWorkType.DISPERSION_OR_SPRAYING:
            setStepKey(WorkStepKey.SPRAYING_PRODUCTS);
            break;
          case CropWorkType.OTHER:
            setStepKey(WorkStepKey.OTHER_PRODUCTS);
            break;
          default:
            setStepKey(WorkStepKey.WORK);
            break;
        }
        break;
      case WorkStepKey.SOWING_PRODUCTS:
      case WorkStepKey.SPRAYING_PRODUCTS:
      case WorkStepKey.OTHER_PRODUCTS:
        setStepKey(WorkStepKey.WORK);
        break;
    }
    setStep((step) => step - 1);
  };

  const submitWork = useCallback<SubmitType<WorkSubmitData>>(
    (stepData) => {
      const filteredFields = Object.values(stepData.fields).filter(
        (field) => field?.field?.value
      );

      setFormData({
        ...formData,
        ...deepCopy({
          ...stepData,
          fields: filteredFields,
        }),
      });

      if (formData.type_id === CropWorkType.SOWING_OR_PLANTING) {
        setStepKey(WorkStepKey.SOWING_PRODUCTS);
      } else if (formData.type_id === CropWorkType.DISPERSION_OR_SPRAYING) {
        setStepKey(WorkStepKey.SPRAYING_PRODUCTS);
      } else if (formData.type_id === CropWorkType.OTHER) {
        setStepKey(WorkStepKey.OTHER_PRODUCTS);
      } else {
        setStepKey(WorkStepKey.DETAILS);
      }
      setStep((step) => step + 1);
      return [of(null)];
    },
    [formData]
  );

  const submitProducts = useCallback<SubmitType<ProductsSubmitData>>(
    (stepData) => {
      stepData.products = stepData.products?.filter(Boolean);
      setFormData({ ...formData, ...deepCopy(stepData) });
      setStepKey(WorkStepKey.DETAILS);
      setStep((step) => step + 1);
      return [of(null)];
    },
    [formData]
  );

  const submitDetails = useCallback<SubmitType<DetailsSubmitData>>(
    (stepData) => {
      setFormData({ ...formData, ...deepCopy(stepData) });
      setStepKey(WorkStepKey.OVERVIEW);
      setStep((step) => step + 1);
      return [of(null)];
    },
    [formData]
  );

  const submitOverview = useCallback<SubmitType<null, Work>>(() => {
    const work = formDataToWork(formData);
    if (formData.id) {
      return [
        workService.update(farmId, formData.id, work),
        (response) =>
          history.push(
            route(CROP_WORK_VIEW, {
              farmId: farmId,
              season: season,
              id: response.id,
            })
          ),
      ];
    } else {
      return [
        workService.store(farmId, work),
        (response) =>
          history.push(
            route(CROP_WORK_VIEW, {
              farmId: farmId,
              season: season,
              id: response.id,
            })
          ),
      ];
    }
  }, [formData, history, farmId, season, workService]);

  if (
    formData === null ||
    formData.fields === null ||
    warehouseProductCount !== updatedRemaindersCount
  ) {
    return null;
  }

  const steps = [{ name: tp('step_work') }];
  if (
    formData.type_id === CropWorkType.SOWING_OR_PLANTING ||
    formData.type_id === CropWorkType.DISPERSION_OR_SPRAYING ||
    formData.type_id === CropWorkType.OTHER
  ) {
    steps.push({ name: tp('step_products') });
  }
  steps.push({ name: tp('step_details') });
  steps.push({ name: tp('step_overview') });

  return (
    <div>
      <StepIndicator steps={steps} currentStep={step} />
      {stepKey === WorkStepKey.WORK && (
        <StepWork formData={formData} onSubmit={submitWork} />
      )}
      {stepKey === WorkStepKey.SOWING_PRODUCTS && (
        <StepSowingProducts
          formData={formData}
          onReturn={returnStep}
          onSubmit={submitProducts}
        />
      )}
      {stepKey === WorkStepKey.SPRAYING_PRODUCTS && (
        <StepSprayingProducts
          formData={formData}
          onReturn={returnStep}
          onSubmit={submitProducts}
        />
      )}
      {stepKey === WorkStepKey.OTHER_PRODUCTS && (
        <StepOtherProducts
          formData={formData}
          onReturn={returnStep}
          onSubmit={submitProducts}
        />
      )}
      {stepKey === WorkStepKey.DETAILS && (
        <StepDetails
          formData={formData}
          onReturn={returnStep}
          onSubmit={submitDetails}
        />
      )}
      {stepKey === WorkStepKey.OVERVIEW && (
        <StepOverview
          formData={formData}
          onReturn={returnStep}
          onSubmit={submitOverview}
        />
      )}
    </div>
  );
}
